import { Link, useSearchParams } from "react-router-dom";
import { RouteFactory } from "@app/routePaths";
import { BreakingNewsPageContentProps } from "@app/types/BreakingNews";
import { NavigationItem } from "@app/types/Navigation";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import { ActiveType } from "@components/Breadcrumb/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import { topMobileNav } from "@components/Header/constant";
import Header from "@components/Header/Header";
import { useCategoriesFromPath } from "@components/Header/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import { useWindowSize } from "@hooks/useWindowSize";
import BreakingNewsAllCaughtUp from "@pages/BreakingNews/components/BreakingNewsAllCaughtUp";
import BreakingNewsStories from "@pages/BreakingNews/components/BreakingNewsStories";
import { getFilteredArticles } from "@pages/BreakingNews/helper";
import PulseEntryButton from "@pages/Pulse/components/PulseEntry/PulseEntry";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import { cn, getStaticSectionJSONLD } from "@util/helpers";
import cx from "classnames";

export default function BreakingNewsPageContent({
  data: {
    title,
    articles: { today, yesterday, thedaybefore },
    sections,
    seoFooterData,
  },
}: BreakingNewsPageContentProps): React.ReactElement {
  const { isScreenLG } = useWindowSize();
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const { parentCategory } = useCategoriesFromPath();
  const parentCategoryData = sectionNavigationItems?.[parentCategory];

  const items: Record<string, NavigationItem> = {};
  sections.forEach((filter) => {
    const data = sectionNavigationItems[filter];

    if (!data) return;

    const params = new URLSearchParams();
    params.append("filter", filter);
    const item = { ...data, link: `/breaking-news?${params.toString()}` };

    items[filter] = item;
  });

  const filtersDataMapping = {
    ...sectionNavigationItems,
    "breaking-news": {
      ...sectionNavigationItems["breaking-news"],
      children: sections,
    },
    ...items,
  };

  const [searchParams] = useSearchParams();

  const activeFilter = searchParams.get("filter") || "";
  const todaysArticles = getFilteredArticles(today, activeFilter);
  const yesterdaysArticles = getFilteredArticles(yesterday, activeFilter);
  const thedaybeforesArticles = getFilteredArticles(thedaybefore, activeFilter);
  const totalArticles =
    todaysArticles.length +
    yesterdaysArticles.length +
    thedaybeforesArticles.length;

  return (
    <div className="w-full" data-testid="breaking-news-page-content-component">
      <AdSettings
        adNames={[
          "lb1",
          "imu1",
          "imu2",
          "imu3",
          "bn1",
          "bn2",
          "bn3",
          "bn4",
          "prestitial",
          "catfish",
          "abm",
        ]}
        path={RouteFactory.breakingNews}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <MetaTags
        title={title}
        ogTitle="The Business Times"
        description="The Business Times - Find latest business & financial news including analysis and opinion on top business stories, stock markets in Singapore, Asia-Pacific & global market news and more at The Business Times."
        ogDescription="Find latest business & financial news including analysis and opinion on top business stories, stock markets in Singapore, Asia-Pacific & global market news and more at The Business Times."
        slug={RouteFactory.breakingNews}
        jsonLd={getStaticSectionJSONLD(
          "Breaking News",
          RouteFactory.breakingNews
        )}
      />

      <GAData title="Breaking_News_Index" level2="breaking news" />

      <div className="w-full">
        <Ad
          adType="lb1"
          className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
        />

        <PulseEntryButton />

        <Header />

        <Container>
          <Row rootClassName="pt-6">
            <Column rootClassName="mb-6 mx-auto lg:hidden ">
              <div className="flex gap-4">
                {topMobileNav.map((nav) => {
                  return (
                    <div className="group" key={nav.key}>
                      <Link
                        key={nav.key}
                        role="link"
                        to={`${nav.link}?ref=home-menubar`}
                        className={cn(
                          "font-poppins text-lg font-medium text-gray-515",
                          {
                            "font-semibold text-gray-850":
                              nav.key === "breaking-news",
                          }
                        )}
                        reloadDocument
                      >
                        {nav.label}
                      </Link>
                      <div
                        className={cx(
                          "mx-auto mt-1 h-[2px] w-[3rem] bg-gray-850 transition-all duration-300",
                          nav.key === "breaking-news"
                            ? "opacity-100"
                            : "opacity-0",
                          "group-hover:opacity-100"
                        )}
                      ></div>
                    </div>
                  );
                })}
              </div>
            </Column>

            <Column rootClassName="w-full mb-6">
              <div className="flex-none">
                <h1
                  className={cx(
                    "hidden pb-3 pt-6 font-playfair text-8xl font-semibold text-gray-850 lg:block"
                  )}
                >
                  <span className="text-10xl">B</span>REAKING{" "}
                  <span className="text-10xl">N</span>EWS
                </h1>
              </div>

              <div>
                <Breadcrumb
                  activeFilter={activeFilter}
                  activeType={ActiveType.QUERY}
                  className="border-b-0 border-t border-gray-175 pt-5 lg:border-b lg:border-t-0 lg:border-gray-850"
                  parentCategory={"breaking-news"}
                  categoryDataMapping={filtersDataMapping}
                  childCategory="international"
                  extraBreadcrumbItems={
                    <div
                      className={cn(
                        "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80",
                        { "font-bold": !activeFilter }
                      )}
                    >
                      <Link
                        to={`${parentCategoryData?.link}?ref=listing-menubar`}
                        reloadDocument
                      >
                        All
                      </Link>
                    </div>
                  }
                />
              </div>
            </Column>

            <Column
              rootClassName="w-full lg:w-8/12 mb-6"
              id="breakingnewspage-content-section"
            >
              <div className="space-y-20">
                {todaysArticles.length > 0 ? (
                  <BreakingNewsStories
                    articles={todaysArticles}
                    group="today"
                    disablePaywall={
                      OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                    }
                  />
                ) : null}

                {yesterdaysArticles.length > 0 ? (
                  <BreakingNewsStories
                    articles={yesterdaysArticles}
                    articlesBefore={todaysArticles.length}
                    group="yesterday"
                    disablePaywall={
                      OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                    }
                    rootClassName="border-gray-850 pt-2 lg:border-t first:border-t-0"
                  />
                ) : null}

                {thedaybeforesArticles.length > 0 ? (
                  <BreakingNewsStories
                    articles={thedaybeforesArticles}
                    articlesBefore={
                      todaysArticles.length + yesterdaysArticles.length
                    }
                    group="thedaybefore"
                    disablePaywall={
                      OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                    }
                    rootClassName="border-gray-850 pt-2 lg:border-t first:border-t-0"
                  />
                ) : null}

                {totalArticles > 0 && totalArticles < 10 ? (
                  <Ad
                    adType="bn1"
                    className="w-[calc(100%+5rem)] -translate-x-20 overflow-hidden bg-white lg:w-auto lg:translate-x-0"
                  />
                ) : null}
              </div>

              <BreakingNewsAllCaughtUp />
            </Column>

            <Column rootClassName="w-full lg:w-4/12 mb-6">
              <div className="h-full">
                {isScreenLG ? (
                  <>
                    {totalArticles <= 18 ? (
                      <Ad adType="imu1" className="lg:sticky lg:top-20" />
                    ) : null}

                    {totalArticles > 18 && totalArticles <= 36 ? (
                      <>
                        <div className="mb-6 lg:h-[800px]">
                          <Ad adType="imu1" className="lg:sticky lg:top-20" />
                        </div>

                        <div className="mb-6 lg:h-[800px]">
                          <Ad adType="imu2" className="lg:sticky lg:top-20" />
                        </div>
                      </>
                    ) : null}

                    {totalArticles > 36 ? (
                      <>
                        <div className="mb-6 lg:h-[800px]">
                          <Ad adType="imu1" className="lg:sticky lg:top-20" />
                        </div>

                        <div className="mb-6 lg:h-[800px]">
                          <Ad adType="imu2" className="lg:sticky lg:top-20" />
                        </div>

                        <Ad adType="imu3" className="lg:sticky lg:top-20" />
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            </Column>
          </Row>
        </Container>

        <SeoFooter data={seoFooterData} />

        <Ad adType="catfish" />

        <Ad adType="abm" />

        <Ad adType="prestitial" />
      </div>
    </div>
  );
}
